import React, { memo, PropsWithChildren, useEffect, useMemo, useState } from 'react';
import { Button } from '@core/components/Button';
import { openAssistantModal } from '../Assistant';
import { useBloc } from '@core/utils/bloc';
import { AssistantButtonBloc } from './AssistantButtonBloc';
import { useObservable } from '@core/utils/hooks/rxjs';
import OpenAI from 'openai';
import { Observable } from 'rxjs';
import { AIToolResponse, OpenAIMessage } from '@business/constants/ai';
import { R } from '@core/utils/r';
import { isTenants } from '@business/services/common_service/common_service';

export interface AssistantButtonProps {
  assistantId: string;
  onDone?: (output: string) => void;
  onInitialMessage?(message: string): string;
  onOutputMessage?(message: string): string;
  clasName?: string;
  tools?: OpenAI.Responses.Tool[];
  onToolCall?(callId: string, fnName: string, args: any): Observable<AIToolResponse>;
  hideBtnIcon?: boolean;
  extraStyles?: React.CSSProperties;
  onRenderMessage?: (message: OpenAIMessage) => React.ReactNode;
  contextId?: string;
}

export const AssistantButton = memo((props: PropsWithChildren<AssistantButtonProps>) => {
  const {
    assistantId,
    onDone,
    onInitialMessage,
    onOutputMessage,
    tools,
    onToolCall,
    hideBtnIcon = false,
    extraStyles,
    onRenderMessage,
    contextId,
  } = props;
  const [initialMessage, setInitialMessage] = useState<string | undefined>(undefined);
  const [outputMessage, setOutputMessage] = useState<string | undefined>(undefined);
  const bloc = useBloc(AssistantButtonBloc, assistantId);
  const assistant = useObservable(bloc.assistant$);

  useEffect(() => {
    if (assistant) {
      setInitialMessage(onInitialMessage ? onInitialMessage(R.trim(assistant.initialMessage || '')) : assistant.initialMessage);
      setOutputMessage(onOutputMessage ? onOutputMessage(assistant.useMessage || '') : assistant.useMessage);
    }
  }, [assistant, onInitialMessage, onOutputMessage]);

  if (!assistant) return null;

  return (
    <Button
      outline
      extraClassNames={props.clasName}
      style={{ ...(extraStyles || {}) }}
      onClick={() =>
        openAssistantModal({
          assistant,
          onDone,
          initialMessage: initialMessage,
          outputMessage: outputMessage,
          tools,
          onRenderMessage,
          onToolCall,
          contextId,
        })
      }
    >
      {!hideBtnIcon && <i className="fad fa-sparkles mr-2"></i>}
      {props.children}
    </Button>
  );
});
